import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { ECommerceProductsTreeMappingService } from '@api-ui-app/src/app/product/product-add/services/e-commerce-products-tree-mapping.service';
import { EProductTreeItem } from '@api-ui-app/src/app/product/product-add/types/e-product-tree-item';
import { EProductAbbridged } from '@gelato-api-ui/core/e-commerce/e-product-abbridged';
import { EProductVariant } from '@gelato-api-ui/core/e-commerce/e-product-variant';
import { EProductWithVariants } from '@gelato-api-ui/core/e-commerce/e-product-with-variants';
import { EStoreType } from '@gelato-api-ui/core/e-commerce/e-store-type.enum';
import { DialogRef } from '@gelato-api-ui/sdk/src/lib/shared/dialog/dialog-ref';
import { DIALOG_DATA } from '@gelato-api-ui/sdk/src/lib/shared/dialog/dialog-tokens';
import { DialogSize } from '@gelato-api-ui/sdk/src/lib/shared/dialog/types/dialog-size';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';

interface ProductTemplatesVariantSelectionModalContext {
  productTemplate: EProductWithVariants;
}

@Component({
  selector: 'gd-product-templates-variant-selection-modal',
  templateUrl: './product-templates-variant-selection-modal.component.html',
  styleUrls: ['./product-templates-variant-selection-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductTemplatesVariantSelectionModalComponent implements OnInit {
  productTemplate$ = new BehaviorSubject<EProductWithVariants>(null);
  size = DialogSize.Tiny;
  productsTreeItem$ = this.productTemplate$.pipe(
    map((productTemplate: EProductWithVariants): EProductTreeItem => {
      if (!productTemplate) {
        return null;
      }

      const tree: EProductTreeItem[] = this.eCommerceProductsTreeMappingService.getProductsTree(
        [productTemplate as unknown as EProductAbbridged],
        productTemplate?.variants,
      );

      return tree?.[0];
    }),
  );

  storeType: EStoreType.TEMPLATE;
  checkedProductVariantIds$ = new BehaviorSubject<string[]>([]);
  selectedProductVariantId$ = this.checkedProductVariantIds$.pipe(
    map((checkedProductVariantIds: string[]): string => checkedProductVariantIds?.[0]),
  );
  selectedProductVariant$ = combineLatest([this.productTemplate$, this.selectedProductVariantId$]).pipe(
    map(([productTemplate, selectedProductVariantId]) =>
      (productTemplate?.variants || []).find(
        (loopProductVariant: EProductVariant): boolean => loopProductVariant?.id === selectedProductVariantId,
      ),
    ),
  );

  constructor(
    private readonly dialogRef: DialogRef,
    private readonly eCommerceProductsTreeMappingService: ECommerceProductsTreeMappingService,
    @Inject(DIALOG_DATA) private readonly dialogData: ProductTemplatesVariantSelectionModalContext,
  ) {}

  ngOnInit() {
    const productTemplate = this.context?.productTemplate;

    const firstProductVariantId: string = productTemplate?.variants?.[0]?.id;

    this.productTemplate$.next(productTemplate);
    this.checkedProductVariantIds$.next(firstProductVariantId ? [firstProductVariantId] : []);
  }

  get context() {
    return this.dialogData;
  }

  continue() {
    this.selectedProductVariant$
      .pipe(
        take(1),
        filter(Boolean),
        tap((productVariant: EProductVariant) => {
          this.dialogRef.close(productVariant);
        }),
      )
      .subscribe();
  }

  cancel() {
    this.dialogRef.close(undefined);
  }

  onCheckedProductVariantIdsChange(checkedProductVariantIds: string[]) {
    this.checkedProductVariantIds$.next(checkedProductVariantIds);
  }
}
